// Built-ins
import { useState } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";

// Global Variables
import { headers } from "../../global_vars";
import { useMergedModalVisibility } from "../../common/useMergedModalVisibility";
import { NESTED_MODAL_PROPS } from "../../common/nestedModalProps";

// Variables
window.Swal = Swal;

// API endpoint - Using regions API
const ADD_COUNTRY = "https://ultima.icsgr.com/api/regions/all_countries/";

// Helpers
const onlyUpperLetters = (value) => value.replace(/[^a-zA-Zα-ωάέήίόύώΐΰ]/gi, "").toUpperCase();
const clampLen = (value, max) => value.slice(0, max);
const toSmallInt = (value) => {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) return "";
  return Math.max(-32768, Math.min(32767, n)); // Django SmallIntegerField range
};

function AddCountryModal({
  onCountryCreated,
  show: showProp,
  onHide: onHideProp,
  hideTrigger = false,
  nested = false,
}) {
  const { show, open, close } = useMergedModalVisibility(showProp, onHideProp);

  const [title, setTitle] = useState("");
  const [currency, setCurrency] = useState("");   // optional
  const [orderindex, setOrderindex] = useState(""); // user must choose

  const resetForm = () => {
    setTitle("");
    setCurrency("");
    setOrderindex("");
  };

  const handleClose = () => {
    close();
    resetForm();
  };
  const handleShow = () => {
    resetForm();
    open();
  };

  const isTitleValid = title.trim().length >= 2 && title.trim().length <= 40;
  const isCurrencyValid = currency.length === 3;
  const isOrderIndexValid = orderindex !== "" && Number.isInteger(+orderindex);
  const isFormValid = isTitleValid && isCurrencyValid && isOrderIndexValid;
  const createNewCountry = async () => {
    try {
      // Update headers with current token
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const res = await axios({
        method: "post",
        url: ADD_COUNTRY,
        headers: currentHeaders,
        data: {
          title: title.trim(),
          currency: currency || null,   // optional
          orderindex: Number(orderindex),
        },
      });

      const created = res?.data;

      // Always close the modal immediately on success, even if callbacks fail.
      handleClose();

      if (typeof onCountryCreated === "function") {
        try {
          await Promise.resolve(onCountryCreated(created));
        } catch (_) {
          // swallow callback failures so the modal stays closed
        }
      } else {
        const newId = created?.country_id || created?.id || "unknown";
        window.location.href = "/regions/country/" + newId;
      }
    } catch (e) {
      // Handle different error response formats
      let apiMsg = "Something went wrong while creating the country.";
      const data = e?.response?.data;

      if (data?.error) {
        apiMsg = data.error;
      } else if (data?.title && Array.isArray(data.title)) {
        apiMsg = data.title[0];
      } else if (data?.country_id && Array.isArray(data.country_id)) {
        apiMsg = data.country_id[0];
      } else if (data?.errormsg) {
        apiMsg = data.errormsg;
      } else if (data?.detail) {
        apiMsg = data.detail;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiMsg,
      });
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
          <BiPlus
            style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}
          />
          Create new Country
        </Button>
      )}

      <GuardedModal
        show={show}
        size="lg"
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        {...(nested ? NESTED_MODAL_PROPS : {})}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create new Country</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Country:</Form.Label>
                  <Form.Control
                    maxLength={40}
                    placeholder="e.g., GREECE"
                    onChange={(e) =>
                      setTitle(clampLen(e.target.value.toUpperCase(), 40))
                    }
                    value={title}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Currency (3 letters):</Form.Label>
                  <Form.Control
                    maxLength={3}
                    placeholder="e.g., EUR"
                    onChange={(e) =>
                      setCurrency(onlyUpperLetters(clampLen(e.target.value, 3)))
                    }
                    value={currency}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Order by (default 0):</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 1"
                    onChange={(e) => setOrderindex(toSmallInt(e.target.value))}
                    value={orderindex}
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {!isTitleValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Title is required (2–40 chars).
                  </li>
                )}
                {!isCurrencyValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Currency is required (3 chars).
                  </li>
                )}
                {!isOrderIndexValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Order by is required.
                  </li>
                )}
              </ul>
            ) : (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "green" }}
              >
                <li>
                  <AiOutlineCheckCircle
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                  Validated
                </li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>
            Close
          </Button>
          <Button
            color="green"
            onClick={createNewCountry}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export default AddCountryModal;
